import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNotificationContext } from '../../../context/NotificationContext';
import { ApiService } from '../../../services/api';
import { TaskLockStatusResponse } from '../../../types';

interface UseTaskLockParams {
  taskId: string | undefined;
  token: string;
  storeId: number | undefined;
  userId: string | undefined;
  isEditMode: boolean;
}

export const useTaskLock = ({ taskId, token, storeId, userId, isEditMode }: UseTaskLockParams) => {
  const { showErrorToast } = useNotificationContext();
  const [taskLockStatus, setTaskLockStatus] = useState<TaskLockStatusResponse | null>(null);
  const [isLocking, setIsLocking] = useState(false);

  const isTaskAssignedToCurrentUser = useMemo(() => {
    if (!userId || !taskLockStatus?.lockedByUserId) return false;
    return userId === taskLockStatus.lockedByUserId;
  }, [taskLockStatus?.lockedByUserId, userId]);

  const isLockedByOther = useMemo(() => {
    if (!taskLockStatus?.lockedByUserId) return false;
    return !isTaskAssignedToCurrentUser;
  }, [taskLockStatus?.lockedByUserId, isTaskAssignedToCurrentUser]);

  // Fetch lock status on mount, and auto-acquire lock if in edit mode
  useEffect(() => {
    const fetchTaskLockStatus = async () => {
      if (!taskId || !token || !storeId) {
        return;
      }

      const parsedTaskId = parseInt(taskId, 10);

      let lockStatus: TaskLockStatusResponse | null = null;
      try {
        lockStatus = await ApiService.getTaskLockStatus(token, parsedTaskId, storeId);
        setTaskLockStatus(lockStatus);
      } catch {}

      if (!isEditMode) {
        return;
      }

      // Edit mode via URL: if locked by someone else, don't attempt to lock
      if (lockStatus?.lockedByUserId && lockStatus.lockedByUserId !== userId) {
        showErrorToast(`Task is already locked by ${lockStatus.lockedByUserName}`);
        return;
      }

      try {
        await ApiService.lockTaskForUser(token, parsedTaskId, storeId);

        const refreshedStatus = await ApiService.getTaskLockStatus(token, parsedTaskId, storeId);
        setTaskLockStatus(refreshedStatus);
      } catch {
        showErrorToast('Failed to lock task for editing');
      }
    };

    fetchTaskLockStatus();
  }, [taskId, token, userId, storeId, isEditMode, showErrorToast]);

  // Acquire lock for the current user (used when entering edit mode)
  const lockTask = useCallback(async (): Promise<boolean> => {
    if (!taskId || !token || !storeId) return false;
    if (isTaskAssignedToCurrentUser) return true;

    const parsedTaskId = parseInt(taskId, 10);
    setIsLocking(true);

    try {
      const lockStatus = await ApiService.getTaskLockStatus(token, parsedTaskId, storeId);
      if (lockStatus.lockedByUserId && lockStatus.lockedByUserId !== userId) {
        showErrorToast(`Task is already locked by ${lockStatus.lockedByUserName}`);
        setTaskLockStatus(lockStatus);
        setIsLocking(false);
        return false;
      }
    } catch {
      setIsLocking(false);
      return false;
    }

    try {
      await ApiService.lockTaskForUser(token, parsedTaskId, storeId);
      const refreshedStatus = await ApiService.getTaskLockStatus(token, parsedTaskId, storeId);
      setTaskLockStatus(refreshedStatus);
    } catch {
      showErrorToast('Failed to lock task for editing');
      setIsLocking(false);
      return false;
    }

    setIsLocking(false);
    return true;
  }, [taskId, token, storeId, userId, isTaskAssignedToCurrentUser, showErrorToast]);

  // Release lock for the current user
  const unlockTask = useCallback(async () => {
    if (!taskId || !token || !storeId) return;

    try {
      await ApiService.unlockTask(token, parseInt(taskId, 10), storeId);
      setTaskLockStatus(null);
    } catch {
      showErrorToast('Failed to unlock task');
    }
  }, [taskId, token, storeId, showErrorToast]);

  // Store latest values in refs for cleanup and refresh interval
  const isTaskAssignedToCurrentUserRef = useRef(isTaskAssignedToCurrentUser);
  const taskIdRef = useRef(taskId);
  const tokenRef = useRef(token);
  const storeIdRef = useRef(storeId);
  const didUnmount = useRef(false);

  useEffect(() => {
    isTaskAssignedToCurrentUserRef.current = isTaskAssignedToCurrentUser;
    taskIdRef.current = taskId;
    tokenRef.current = token;
    storeIdRef.current = storeId;
  }, [isTaskAssignedToCurrentUser, taskId, token, storeId]);

  // Refresh lock before TTL expires to prevent it from expiring during long editing sessions
  useEffect(() => {
    if (!isTaskAssignedToCurrentUser || !taskId || !token || !storeId || !taskLockStatus?.ttl) {
      return;
    }

    // Refresh at 2/3 of the TTL to leave a comfortable margin, but no more often than every 60s
    const refreshMs = Math.max((taskLockStatus.ttl * 1000 * 2) / 3, 5000);

    const intervalId = setInterval(async () => {
      try {
        const parsedTaskId = parseInt(taskId, 10);
        await ApiService.lockTaskForUser(token, parsedTaskId, storeId);
        const refreshedStatus = await ApiService.getTaskLockStatus(token, parsedTaskId, storeId);
        setTaskLockStatus(refreshedStatus);
      } catch {
        showErrorToast('Failed to refresh task lock. Your lock may have expired.');
      }
    }, refreshMs);

    return () => clearInterval(intervalId);
  }, [isTaskAssignedToCurrentUser, taskId, token, storeId, taskLockStatus?.ttl, showErrorToast]);

  // Cleanup: unlock task when leaving the page (only on unmount, if locked by current user)
  useEffect(() => {
    return () => {
      if (
        isTaskAssignedToCurrentUserRef.current &&
        taskIdRef.current &&
        tokenRef.current &&
        storeIdRef.current &&
        (process.env.NODE_ENV !== 'development' || didUnmount.current)
      ) {
        ApiService.unlockTask(
          tokenRef.current,
          parseInt(taskIdRef.current, 10),
          storeIdRef.current
        ).catch(() => {});
      }

      didUnmount.current = true;
    };
  }, []);

  return {
    taskLockStatus,
    isLocking,
    isTaskAssignedToCurrentUser,
    isLockedByOther,
    lockTask,
    unlockTask,
  };
};
